(() => {
  if (window.MyHoneyCart) return;

  class MyHoneyBasket {
    constructor(root) {
      this.root = root;
      this.dialog = root.querySelector('dialog');
      this.config = JSON.parse(root.querySelector('[data-mh-basket-config]').textContent);
      this.revision = 0;
      this.pending = null;
      this.trigger = null;
      this.messages = this.config.messages;
      this.dialog.addEventListener('close', () => {
        document.documentElement.classList.remove('mh-basket-open');
        if (this.trigger?.isConnected) this.trigger.focus({ preventScroll: true });
        document.dispatchEvent(new CustomEvent('mh:cart:close'));
      });
      this.dialog.addEventListener('pointerdown', event => {
        if (event.target !== this.dialog) return;
        const rect = this.dialog.getBoundingClientRect();
        if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) this.dialog.close();
      });
      this.dialog.addEventListener('click', event => this.handleClick(event));
      this.dialog.addEventListener('submit', event => this.handleSubmit(event));
      document.addEventListener('click', event => {
        const trigger = event.target.closest('[data-mh-cart-open]');
        if (!trigger || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        event.preventDefault();
        this.open(trigger);
      });
      this.readCount();
    }

    show(trigger) {
      if (!this.dialog.open) {
        this.trigger = trigger || document.activeElement;
        this.dialog.showModal();
        document.documentElement.classList.add('mh-basket-open');
        document.dispatchEvent(new CustomEvent('mh:cart:open'));
      }
    }

    async open(trigger) {
      this.show(trigger);
      if (this.pending) return;
      const revision = this.revision;
      const rendered = await this.refresh(revision);
      if (revision === this.revision && !rendered) this.recovery(this.messages.refreshError);
    }

    status(message = '') {
      this.dialog.querySelector('[data-mh-basket-status]').textContent = message;
    }

    recovery(message) {
      const content = this.dialog.querySelector('[data-mh-basket-content]');
      const recovery = this.dialog.querySelector('[data-mh-basket-recovery]');
      const focusWasHidden = content.contains(document.activeElement);
      content.hidden = true;
      recovery.hidden = false;
      this.status(message);
      if (this.dialog.open && focusWasHidden) recovery.querySelector('a').focus({ preventScroll: true });
    }

    setBusy(value) {
      this.dialog.setAttribute('aria-busy', String(value));
      if (value && this.dialog.contains(document.activeElement)) {
        this.focusBeforeUpdate = {
          key: document.activeElement.dataset.lineKey,
          label: document.activeElement.getAttribute('aria-label')
        };
      }
      this.dialog.querySelectorAll('[data-mh-basket-content] button').forEach(button => {
        if (value) {
          button.dataset.mhPreviouslyDisabled = String(button.disabled);
          button.disabled = true;
        } else if (button.dataset.mhPreviouslyDisabled !== undefined) {
          button.disabled = button.dataset.mhPreviouslyDisabled === 'true';
          delete button.dataset.mhPreviouslyDisabled;
        }
      });
    }

    readCount() {
      try {
        const state = JSON.parse(this.dialog.querySelector('[data-mh-basket-state]').textContent);
        this.updateCount(state.count);
        return state;
      } catch { return null; }
    }

    updateCount(count) {
      if (!Number.isSafeInteger(count) || count < 0) return;
      document.querySelectorAll('[data-mh-cart-count]').forEach(node => {
        node.textContent = String(count);
        node.hidden = count === 0;
      });
      document.querySelectorAll('[data-mh-cart-label-template]').forEach(node => {
        node.setAttribute('aria-label', node.dataset.mhCartLabelTemplate.replaceAll('[count]', String(count)));
      });
    }

    render(html, revision) {
      if (revision !== this.revision || typeof html !== 'string' || !html.trim()) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('[data-mh-basket-content]');
      if (!content?.querySelector('[data-mh-basket-state]')) return false;
      try {
        const state = JSON.parse(content.querySelector('[data-mh-basket-state]').textContent);
        if (!Number.isSafeInteger(state.count) || state.count < 0 || !Number.isSafeInteger(state.total)) return false;
      } catch { return false; }
      const focused = this.dialog.contains(document.activeElement) ? document.activeElement : null;
      const focusKey = this.focusBeforeUpdate?.key || focused?.dataset.lineKey;
      const focusLabel = this.focusBeforeUpdate?.label || focused?.getAttribute('aria-label');
      const oldContent = this.dialog.querySelector('[data-mh-basket-content]');
      const focusWasReplaced = focused && oldContent.contains(focused);
      oldContent.replaceWith(content);
      content.hidden = false;
      this.dialog.querySelector('[data-mh-basket-recovery]').hidden = true;
      this.readCount();
      if ((focusWasReplaced || focusKey) && this.dialog.open) {
        const newFocus = [...content.querySelectorAll('[data-line-key]')].find(node => node.dataset.lineKey === focusKey && node.getAttribute('aria-label') === focusLabel);
        (newFocus || this.dialog.querySelector('[data-mh-basket-close]')).focus({ preventScroll: true });
      }
      this.focusBeforeUpdate = null;
      return true;
    }

    async request(url, options = {}) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', ...options, signal: controller.signal });
        let data;
        try { data = await response.json(); } catch {
          throw Object.assign(new Error(this.messages.uncertain), { uncertain: true });
        }
        if (response.status >= 500 || data.status >= 500) throw Object.assign(new Error(this.messages.uncertain), { uncertain: true });
        if (!response.ok || data.status >= 400) {
          const description = typeof data.description === 'string' ? data.description : typeof data.message === 'string' ? data.message : this.messages.error;
          throw Object.assign(new Error(description), { apiError: true, status: response.status });
        }
        return data;
      } catch (error) {
        if (error.apiError) throw error;
        throw Object.assign(new Error(this.messages.uncertain), { uncertain: true });
      } finally { clearTimeout(timeout); }
    }

    async refresh(revision = this.revision) {
      const url = new URL(window.location.href);
      url.searchParams.set('sections', this.config.sectionId);
      url.searchParams.delete('section_id');
      try {
        // Product URLs negotiate product JSON for application/json before section rendering.
        const sections = await this.request(url.pathname + url.search, { cache: 'no-store', headers: { Accept: 'text/html' } });
        if (this.render(sections[this.config.sectionId], revision)) return true;
      } catch {}
      if (revision !== this.revision) return false;
      try {
        const cartUrl = `${this.config.root.replace(/\/$/, '')}/cart.js`;
        const cart = await this.request(cartUrl, { cache: 'no-store', headers: { Accept: 'application/json' } });
        if (revision === this.revision) this.updateCount(cart.item_count);
      } catch {}
      return false;
    }

    fingerprint(data) {
      return JSON.stringify([...data.entries()].filter(([key]) => !['sections', 'sections_url'].includes(key)).map(([key, value]) => [key, typeof value === 'string' ? value : [value.name, value.size, value.lastModified]]).sort((a, b) => a[0].localeCompare(b[0])));
    }

    run(signature, operation) {
      if (this.pending) {
        if (this.pending.signature === signature) return this.pending.promise;
        return Promise.reject(new Error(this.messages.busy));
      }
      const revision = ++this.revision;
      this.status();
      this.setBusy(true);
      const promise = Promise.resolve().then(() => operation(revision)).finally(() => {
        this.pending = null;
        this.setBusy(false);
      });
      this.pending = { signature, promise };
      return promise;
    }

    add(formData, trigger) {
      const data = new FormData();
      for (const [key, value] of formData.entries()) data.append(key, value);
      const signature = `add:${this.fingerprint(data)}`;
      data.set('sections', this.config.sectionId);
      data.set('sections_url', window.location.pathname);
      return this.run(signature, async revision => {
        let result;
        try {
          result = await this.request(this.config.addUrl, { method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body: data });
        } catch (error) {
          if (error.uncertain) {
            const rendered = await this.refresh(revision);
            this.show(trigger);
            if (!rendered) this.recovery(error.message);
            else this.status(error.message);
          }
          throw error;
        }
        const rendered = this.render(result.sections?.[this.config.sectionId], revision) || await this.refresh(revision);
        this.show(trigger);
        if (!rendered) this.recovery(this.messages.refreshError);
        else this.status(this.messages.added);
        document.dispatchEvent(new CustomEvent('mh:cart-updated', { detail: { source: 'add', rendered } }));
        return { added: true, rendered };
      });
    }

    change(key, quantity) {
      if (!key || !Number.isSafeInteger(quantity) || quantity < 0) return Promise.reject(new Error(this.messages.error));
      return this.run(`change:${key}:${quantity}`, async revision => {
        try {
          const result = await this.request(this.config.changeUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ id: key, quantity, sections: this.config.sectionId, sections_url: window.location.pathname })
          });
          const rendered = this.render(result.sections?.[this.config.sectionId], revision) || await this.refresh(revision);
          this.updateCount(result.item_count);
          if (!rendered) this.recovery(this.messages.refreshError);
          else this.status(this.messages.updated);
          document.dispatchEvent(new CustomEvent('mh:cart-updated', { detail: { source: 'change', rendered } }));
          return { updated: true, rendered };
        } catch (error) {
          const rendered = await this.refresh(revision);
          if (!rendered) this.recovery(error.message);
          else this.status(error.message);
          throw error;
        }
      });
    }

    handleClick(event) {
      if (event.target.closest('[data-mh-basket-close]')) this.dialog.close();
      const change = event.target.closest('[data-mh-basket-change]');
      if (!change) return;
      event.preventDefault();
      if (this.pending || change.disabled) return;
      this.change(change.dataset.lineKey, Number(change.dataset.mhBasketChange)).catch(error => this.status(error.message));
    }

    handleSubmit(event) {
      const form = event.target.closest('[data-mh-basket-addon]');
      if (!form) {
        if (this.pending) event.preventDefault();
        return;
      }
      event.preventDefault();
      if (this.pending) return;
      this.add(new FormData(form), event.submitter).catch(error => this.status(error.message));
    }
  }

  const root = document.querySelector('[data-mh-basket-root]');
  if (!root) return;
  const basket = new MyHoneyBasket(root);
  window.MyHoneyCart = {
    add: (data, trigger) => basket.add(data, trigger),
    open: trigger => basket.open(trigger),
    refresh: () => basket.refresh()
  };
})();


